import { createContext, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';

import { usePersistedState } from '../hooks';
import { SITE_TIMEZONE, startOfDayInTz, subDays } from '../utils/dateUtils';
import getLoopIsDisabled from '../utils/getLoopIsDisabled';

const AppContext = createContext();

const AppProvider = ({ children }) => {
  const theme = useTheme();
  const [pageTitle, setPageTitle] = usePersistedState('pageTitle', `${theme.language.appName}`);
  const [userSub, setUserSub] = usePersistedState('userSub', null);

  const [selectedDate, setSelectedDate] = useState(() => subDays(startOfDayInTz(SITE_TIMEZONE), 1));
  const [savingsLoading, setSavingsLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [optimiserSettings, setOptimiserSettings] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [analyticsStartDate, setAnalyticsStartDate] = usePersistedState('analyticsStartDate', null);
  const [datePickerWarning, setDatePickerWarning] = useState(null);
  const [hideTdasBanner, setHideTdasBanner] = usePersistedState('hideTdasBanner', null);
  const [showTdasModal, setShowTdasModal] = usePersistedState('showTdasModal', null);
  const [hasShownTdasModal, setHasShownTdasModal] = usePersistedState('hasShownTdasModal', null);
  const [loopUserAccount, setLoopUserAccount] = usePersistedState('loopUserAccount', null);
  const [userMpan, setUserMpan] = usePersistedState('userMpan', null);
  const [showTdasAlert, setShowTdasAlert] = usePersistedState('showTdasAlert', null);
  const [campaigns, setCampaigns] = usePersistedState('campaigns', null);
  const [userCampaigns, setUserCampaigns] = usePersistedState('campaigns', null);
  const [tdasCampaign, setTdasCampaign] = usePersistedState('campaigns', null);
  const [tdasIsActive, setTdasIsActive] = usePersistedState('tdasIsActive', false);
  const [userEvents, setUserEvents] = usePersistedState('userEvents', null);
  const [tdasModalView, setTdasModalView] = useState(null);
  const [gridEvent, setGridEvent] = useState(null);
  const [systemFeatures, setSystemFeatures] = usePersistedState('systemFeatures', null);
  const [showSimulationMode, setShowSimulationMode] = usePersistedState('showSimulationMode', null);
  const [showTariffRecommender, setShowTariffRecommender] = usePersistedState('showTariffRecommender', null);
  const [recommendedTariffs, setRecommendedTariffs] = usePersistedState('recommendedTariffs', null);
  const [loadingRecommendedTariffs, setLoadingRecommendedTariffs] = usePersistedState(
    'loadingRecommendedTariffs',
    true
  );
  const [tariffSimResponseGeneratedDate, setTariffSimResponseGeneratedDate] = usePersistedState(
    'tariffSimResponseGeneratedDate',
    null
  );
  const [loopIsDisabled, setLoopIsDisabled] = usePersistedState('loopIsDisabled', true);
  const [twentyFourHoursOfData, setTwentyFourHoursOfData] = usePersistedState('twentyFourHoursOfData', null);
  const [showSavings, setShowSavings] = usePersistedState('showSavings', false);
  const [fetchingTwentyFourHoursOfData, setFetchingTwentyFourHoursOfData] = usePersistedState(
    'fetchingTwentyFourHoursOfData',
    false
  );
  const [latestRecord, setLatestRecord] = usePersistedState('latestRecord', false);
  const [fetchingBehaviourForecast, setFetchingBehaviourForecast] = useState(false);
  const [forecastIsStaleError, setForecastIsStaleError] = useState(false);

  const [appThemeName, setAppThemeName] = usePersistedState('appThemeName', null);
  const [isOnboarded, setIsOnboarded] = usePersistedState('isOnboarded', false);
  const [subscriptionsDisabled, setSubscriptionsDisabled] = usePersistedState('subscriptionsDisabled', false);

  const [currentUserInfo, setCurrentUserInfo] = usePersistedState('currentUserInfo', undefined);
  const [activeSubscription, setActiveSubscription] = usePersistedState('activeSubscription', undefined);

  useEffect(() => {
    if (!!userDetails) {
      const loopIsDisabled = getLoopIsDisabled(showSimulationMode, userDetails);
      setLoopIsDisabled(loopIsDisabled);
    }
  }, [setLoopIsDisabled, showSimulationMode, userDetails]);

  useEffect(() => {
    if (!!userDetails) {
      const shouldShowSavings = !loopIsDisabled && !!userDetails?.analytics_data_range?.end_date;
      setShowSavings(shouldShowSavings);
    }
  }, [loopIsDisabled, setShowSavings, userDetails]);

  const state = {
    pageTitle,
    setPageTitle,
    selectedDate,
    setSelectedDate,
    savingsLoading,
    setSavingsLoading,
    userDetails,
    setUserDetails,
    userSub,
    setUserSub,
    analyticsStartDate,
    setAnalyticsStartDate,
    isAdmin,
    setIsAdmin,
    datePickerWarning,
    setDatePickerWarning,
    hideTdasBanner,
    setHideTdasBanner,
    showTdasModal,
    setShowTdasModal,
    hasShownTdasModal,
    setHasShownTdasModal,
    tdasModalView,
    setTdasModalView,
    loopUserAccount,
    setLoopUserAccount,
    showTdasAlert,
    setShowTdasAlert,
    campaigns,
    setCampaigns,
    tdasCampaign,
    setTdasCampaign,
    userCampaigns,
    setUserCampaigns,
    tdasIsActive,
    setTdasIsActive,
    userEvents,
    setUserEvents,
    userMpan,
    setUserMpan,
    optimiserSettings,
    setOptimiserSettings,
    gridEvent,
    setGridEvent,
    systemFeatures,
    setSystemFeatures,
    showSimulationMode,
    setShowSimulationMode,
    recommendedTariffs,
    setRecommendedTariffs,
    loadingRecommendedTariffs,
    setLoadingRecommendedTariffs,
    tariffSimResponseGeneratedDate,
    setTariffSimResponseGeneratedDate,
    showTariffRecommender,
    setShowTariffRecommender,
    loopIsDisabled,
    setLoopIsDisabled,
    twentyFourHoursOfData,
    setTwentyFourHoursOfData,
    showSavings,
    fetchingTwentyFourHoursOfData,
    setFetchingTwentyFourHoursOfData,
    latestRecord,
    setLatestRecord,
    fetchingBehaviourForecast,
    setFetchingBehaviourForecast,
    forecastIsStaleError,
    setForecastIsStaleError,
    appThemeName,
    setAppThemeName,
    isOnboarded,
    setIsOnboarded,
    subscriptionsDisabled,
    setSubscriptionsDisabled,
    currentUserInfo,
    setCurrentUserInfo,
    activeSubscription,
    setActiveSubscription,
  };

  return <AppContext.Provider value={state}>{children}</AppContext.Provider>;
};

export { AppContext, AppProvider };
